import { createResource } from 'redux-rest-resource'
import {
  remove,
  includes,
  map,
  get,
  cloneDeep,
  toNumber,
  defaultTo
} from 'lodash'
import { baseURL } from './'
import I18n from 'I18n'
import { message } from 'antd'

export const fetchCompanyPdpNums = async (fetchCompanyPdpnums, id) => {
  try {
    if (!id) {
      return
    }

    const companyPdpnumsData = await fetchCompanyPdpnums(id)
    const companyPdpnums = companyPdpnumsData?.body || []
    const uniquePdpnums = Array.from(
      new Map(companyPdpnums.map(p => [p.id, p])).values()
    )
    return uniquePdpnums
  } catch (error) {
    console.error('Error fetching company PDP numbers:', error)
    message.error(I18n.t('api.errors.preventionPlan.fetchPdpnums'))
    return error
  }
}

export const fetchCompaniesPdpNums = async (fetchCompanyPdpnums, ids) => {
  try {
    if (!ids?.length) {
      return
    }

    const allCompaniesPdpnumsData = await Promise.all(
      ids.map(id => fetchCompanyPdpnums(id))
    )

    const allCompaniesPdpnums = allCompaniesPdpnumsData.reduce(
      (acc, { body }) => [...acc, ...body],
      []
    )

    const uniquePdpnums = Array.from(
      new Map(allCompaniesPdpnums.map(p => [p.id, p])).values()
    )
    return uniquePdpnums
  } catch (error) {
    console.error('Error fetching companies PDP numbers:', error)
    message.error(I18n.t('api.errors.preventionPlan.fetchPdpnums'))
    return error
  }
}

export const { types, actions, rootReducer } = createResource({
  name: 'company',
  pluralName: 'companies',
  url: `${baseURL}/companies`,
  credentials: 'include',
  actions: {
    fetch: {
      method: 'GET',
      gerundName: 'fetching',
      url: '.',
      reduce: ({ items = [], ...state }, { status, ...response }) => {
        const isFetching = status === 'pending'
        let totalCompanies = 0

        if (status === 'resolved') {
          items = response.body.results
          totalCompanies = response.body.count
        }

        return { ...state, items, isFetching, totalCompanies }
      }
    },
    fetchAll: {
      isArray: true,
      method: 'GET',
      gerundName: 'fetchingAll',
      url: './all',
      reduce: ({ items = [], ...state }, { status, ...response }) => {
        const isFetching = status === 'pending'
        let totalCompanies = 0

        if (status === 'resolved') {
          items = response.body
          totalCompanies = response.body.length
        }

        return { ...state, items, isFetching, totalCompanies }
      }
    },
    fetchArchives: {
      isArray: true,
      method: 'GET',
      gerundName: 'fetching',
      url: './archive',
      reduce: (
        { archives = [], ...state },
        { body: archivedItems, status }
      ) => {
        const isFetching = status === 'pending'

        if (status === 'resolved') {
          archives = archivedItems
        }

        return { ...state, archives, isFetching }
      }
    },
    get: { method: 'GET', gerundName: 'getting', url: './:id' },
    update: {
      method: 'PATCH',
      gerundName: 'updating',
      url: './:id',
      assignResponse: true,
      reduce: ({ items, item, ...state }, { body: updatedItem, status }) => {
        const isUpdating = status === 'pending'

        if (status === 'resolved') {
          item = updatedItem
          items = map(items, item =>
            get(item, 'id') === get(updatedItem, 'id') ? updatedItem : item
          )
        }

        return { ...state, items, item, isUpdating }
      }
    },
    create: {
      method: 'POST',
      gerundName: 'creating',
      url: '.'
    },
    order: {
      method: 'PATCH',
      gerundName: 'ordering',
      url: './order'
    },
    archive: {
      method: 'PATCH',
      gerundName: 'archiving',
      url: './:id/archive',
      reduce: ({ items, ...state }, { context: archivedItem, status }) => {
        if (status === 'resolved') {
          // Suppression des données archivées du store
          remove(items, {
            id: defaultTo(get(archivedItem, 'id'), toNumber(archivedItem))
          })
        }

        return { ...state, items: cloneDeep(items) }
      }
    },
    archiveMany: {
      isArray: true,
      alias: 'archive',
      method: 'PATCH',
      gerundName: 'archiving',
      url: './archive',
      reduce: ({ items, ...state }, { context: archivedItems, status }) => {
        if (status === 'resolved') {
          // Suppression des données archivées du store
          remove(items, ({ id }) =>
            includes(
              map(archivedItems, archivedItem =>
                defaultTo(get(archivedItem, 'id'), toNumber(archivedItem))
              ),
              toNumber(id)
            )
          )
        }

        return { ...state, items: cloneDeep(items) }
      }
    },
    delete: {
      method: 'DELETE',
      gerundName: 'deleting',
      url: './:id',
      reduce: ({ items, ...state }, { context: deletedItem, status }) => {
        if (status === 'resolved') {
          // Suppression des données archivées du store
          remove(items, {
            id: defaultTo(get(deletedItem, 'id'), toNumber(deletedItem))
          })
        }

        return { ...state, items: cloneDeep(items) }
      }
    },
    deleteMany: {
      isArray: true,
      alias: 'delete',
      method: 'DELETE',
      gerundName: 'deleting',
      url: './delete',
      reduce: ({ items, ...state }, { context: deletedItems, status }) => {
        if (status === 'resolved') {
          // Suppression des données archivées du store
          remove(items, ({ id }) =>
            includes(
              map(deletedItems, deletedItem =>
                defaultTo(get(deletedItem, 'id'), toNumber(deletedItem))
              ),
              toNumber(id)
            )
          )
        }

        return { ...state, items: cloneDeep(items) }
      }
    },
    fetchCompanyPdpnums: {
      method: 'GET',
      name: 'fetchCompanyPdpnums',
      gerundName: 'fetchingCompanyPdpnums',
      url: './:id/active_pdp',
      reduce: (state, { body, status }) => {
        if (status === 'pending' || status === 'rejected') {
          return { ...state, isFetchingCompanyPdpnums: status === 'pending' }
        } else if (status === 'resolved') {
          const availablePdpnums = body || []
          return {
            ...state,
            availablePdpnums,
            isFetchingCompanyPdpnums: false
          }
        }
        return state
      }
    }
  }
})
